import { Link, RichTextEditor, RichTextEditorProps } from '@mantine/tiptap';
import Placeholder from '@tiptap/extension-placeholder';
import SubScript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import stylesColors from 'common/styles/styles.colors';
import CustomFormControl, {
  TextLabelPlacementEnum,
} from 'components/elements/custom-form-control';
import { BaseElementInputProps } from 'components/elements/field-type';
import { FormContext } from 'components/elements/form';
import { styles } from 'components/elements/styles';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';

type ControlType =
  | 'default'
  | 'bulletList'
  | 'textAlignment'
  | 'heading'
  | 'link'
  | 'superscript'
  | 'subscript';

export interface RichTextInputProps
  extends BaseElementInputProps,
    Omit<RichTextEditorProps, 'type' | 'children' | 'editor'> {
  disabled?: boolean;
  label?: string;
  type: 'editor';
  required?: boolean;
  noMargin?: boolean;
  labelPlacement?: TextLabelPlacementEnum;
  controls: ControlType[];
  hideLabel?: boolean;
}

export default function RichTextInput(props: RichTextInputProps) {
  const { label, name, disabled, controls, placeholder, ...restProps } = props;

  const { control } = useFormContext<any>();
  const { field, fieldState } = useController({
    name,
    control,
  });
  const context = React.useContext(FormContext);

  const shouldShowError = !!fieldState.error?.message;

  const _disabled = !context.editable || disabled;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    onUpdate(props) {
      const html = props.editor.getHTML();
      field.onChange(html);
    },
    editable: !_disabled,
    content: field.value,
  });

  const disabledControlProps = _disabled
    ? { interactive: false, active: false, disabled: true }
    : {};

  return (
    <CustomFormControl
      {...restProps}
      label={label}
      error={fieldState.error?.message}
      disabled={_disabled}
    >
      <RichTextEditor
        {...restProps}
        editor={editor}
        styles={{
          root: {
            ...(_disabled
              ? { ...styles.reset, cursor: 'not-allowed' }
              : {
                  border: `1px solid ${stylesColors.skyDefault}`,
                  borderRadius: '8px',
                }),
            ...(shouldShowError ? { border: '1px solid red' } : {}),
          },
          toolbar: {
            ...(_disabled
              ? {
                  backgroundColor: '#EEEEEE',
                  borderBottomColor: '#AFAFAF',
                }
              : { borderRadius: '8px 8px 0 0' }),
          },
          control: {
            ...(_disabled
              ? {
                  backgroundColor: '#EEEEEE',
                  borderColor: '#AFAFAF',
                  cursor: 'not-allowed',
                }
              : {}),
          },
          content: {
            minHeight: '76px',
            ...(_disabled
              ? {
                  borderBottomColor: '#AFAFAF',
                  backgroundColor: '#EEEEEE',
                }
              : { borderRadius: '8px' }),
          },
        }}
      >
        {!_disabled && (
          <RichTextEditor.Toolbar>
            {controls.includes('default') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold {...disabledControlProps} />
                <RichTextEditor.Italic {...disabledControlProps} />
                <RichTextEditor.Strikethrough {...disabledControlProps} />
                <RichTextEditor.Underline {...disabledControlProps} />
                <RichTextEditor.OrderedList {...disabledControlProps} />
                {controls.includes('bulletList') && (
                  <RichTextEditor.BulletList {...disabledControlProps} />
                )}
              </RichTextEditor.ControlsGroup>
            )}

            {controls.includes('textAlignment') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft {...disabledControlProps} />
                <RichTextEditor.AlignCenter {...disabledControlProps} />
                <RichTextEditor.AlignRight {...disabledControlProps} />
                <RichTextEditor.AlignJustify {...disabledControlProps} />
              </RichTextEditor.ControlsGroup>
            )}

            {(controls.includes('subscript') ||
              controls.includes('superscript')) && (
              <RichTextEditor.ControlsGroup>
                {controls.includes('subscript') && (
                  <RichTextEditor.Subscript {...disabledControlProps} />
                )}
                {controls.includes('superscript') && (
                  <RichTextEditor.Superscript {...disabledControlProps} />
                )}
              </RichTextEditor.ControlsGroup>
            )}

            {controls.includes('heading') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 {...disabledControlProps} />
                <RichTextEditor.H2 {...disabledControlProps} />
                <RichTextEditor.H3 {...disabledControlProps} />
                <RichTextEditor.H4 {...disabledControlProps} />
                <RichTextEditor.H5 {...disabledControlProps} />
                <RichTextEditor.H6 {...disabledControlProps} />
              </RichTextEditor.ControlsGroup>
            )}

            {controls.includes('link') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link {...disabledControlProps} />
                <RichTextEditor.Unlink {...disabledControlProps} />
              </RichTextEditor.ControlsGroup>
            )}
          </RichTextEditor.Toolbar>
        )}

        <RichTextEditor.Content />
      </RichTextEditor>
    </CustomFormControl>
  );
}
